import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { XCircle, AlertTriangle } from 'lucide-react';
export function ProblemSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <section className="py-24 px-4 bg-[#111827] relative border-t border-white/5" data-id="element-116">
      <div className="max-w-4xl mx-auto" data-id="element-117">
        {/* Opening Hook */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={itemVariants} className="mb-20 pl-6 md:pl-8 border-l-4 border-[#00d4ff] py-2" data-id="element-118">
          <p className="text-xl md:text-2xl text-slate-300 italic leading-relaxed" data-id="element-119">
            Te despiertas a las 6:30 AM y ya te sientes derrotado. No es solo el
            cansancio físico; es esa neblina mental que te persigue a la oficina
            y ese desinterés silencioso que aparece cuando cae la noche. Sientes
            que el "fuego" se ha convertido en una pequeña brasa. Miras a tu
            pareja y, aunque la deseas, tu cuerpo simplemente no responde con la
            intensidad de antes.{' '}
            <strong className="text-white font-bold not-italic" data-id="element-120">
              No estás viejo, estás descalibrado.
            </strong>
          </p>
        </motion.div>

        {/* The Problem */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="mb-24" data-id="element-121">
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-8" data-id="element-122">
            El mundo moderno está diseñado para{' '}
            <span className="text-red-500" data-id="element-123">castrar biológicamente</span> al
            hombre trabajador.
          </motion.h2>

          <motion.p variants={itemVariants} className="text-lg text-slate-300 leading-relaxed mb-10" data-id="element-124">
            El estrés constante del trabajo, las deudas y la falta de sueño
            mantienen una hormona llamada Cortisol por las nubes.
            Biológicamente, es imposible tener Cortisol alto y Testosterona alta
            al mismo tiempo. Mientras vivas en modo "supervivencia", tu cuerpo
            apagará tu vigor, almacenará grasa abdominal y matará tu libido para
            ahorrar energía. Estás conduciendo un Ferrari con el freno de mano
            puesto.
          </motion.p>

          {/* Cortisol vs T Bars */}
          <motion.div variants={itemVariants} className="bg-[#0a0e1a] p-6 md:p-8 rounded-xl border border-white/10" data-id="element-125">
            <div className="mb-6" data-id="element-126">
              <div className="flex justify-between text-sm font-bold uppercase tracking-wider mb-2" data-id="element-127">
                <span className="text-red-500 flex items-center gap-2" data-id="element-128">
                  <AlertTriangle size={16} data-id="element-129" /> Cortisol (Estrés)
                </span>
                <span className="text-red-500" data-id="element-130">Nivel Crítico</span>
              </div>
              <div className="h-4 bg-white/5 rounded-full overflow-hidden" data-id="element-131">
                <motion.div initial={{
                width: 0
              }} whileInView={{
                width: '90%'
              }} transition={{
                duration: 1.5,
                delay: 0.5
              }} className="h-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]" data-id="element-132" />
              </div>
            </div>

            <div data-id="element-133">
              <div className="flex justify-between text-sm font-bold uppercase tracking-wider mb-2" data-id="element-134">
                <span className="text-[#00d4ff]" data-id="element-135">Testosterona (Vigor)</span>
                <span className="text-slate-500" data-id="element-136">Suprimida</span>
              </div>
              <div className="h-4 bg-white/5 rounded-full overflow-hidden" data-id="element-137">
                <motion.div initial={{
                width: 0
              }} whileInView={{
                width: '20%'
              }} transition={{
                duration: 1.5,
                delay: 0.5
              }} className="h-full bg-[#00d4ff] shadow-[0_0_10px_rgba(0,212,255,0.5)]" data-id="element-138" />
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Why Others Failed */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} data-id="element-139">
          <motion.h3 variants={itemVariants} className="text-2xl md:text-3xl font-bold text-white uppercase tracking-tight mb-8" data-id="element-140">
            Por qué lo que has intentado{' '}
            <span className="text-red-500" data-id="element-141">no funcionó</span>
          </motion.h3>

          <div className="space-y-6" data-id="element-142">
            {[{
            title: 'Las pastillas azules',
            desc: 'son solo un parche peligroso que no arregla el motor, solo fuerza la tubería.'
          }, {
            title: "Los suplementos 'T-Boosters' de farmacia",
            desc: 'son basura si no bajas primero tus niveles de estrés.'
          }, {
            title: 'El cardio excesivo en el gimnasio',
            desc: 'en realidad eleva tu cortisol y quema tu testosterona.'
          }, {
            title: "Las dietas de 'ensaladas'",
            desc: 'te privan de las grasas saludables que tus testículos necesitan para fabricar vigor.'
          }].map((item, idx) => <motion.div key={idx} variants={itemVariants} className="flex items-start gap-4 bg-[#0a0e1a]/50 p-5 rounded-lg border border-red-500/20" data-id="element-143">
                <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-1" data-id="element-144" />
                <p className="text-slate-300 text-lg" data-id="element-145">
                  <span className="line-through text-slate-500 font-bold mr-2" data-id="element-146">
                    {item.title}
                  </span>
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}